import {
  MapDrawerEntityForPoi,
  OverlappingFeatureAction,
  StandardPoi,
} from 'features/mapping/mapping.types';
import { OverlappingFeaturesState } from './useOverlappingFeaturesState';

/** Index `step` places from `index`, wrapping past either end. */
export const getCyclicIndex = ({
  index,
  length,
  step,
}: {
  index: number;
  length: number;
  step: number;
}): number => (index + step + length) % length;

const getPoiKey = (poi: StandardPoi): string | null => {
  // `legacyPlace` carries its id on the metadata rather than on the POI.
  const metadataId = (poi.metadata as { id?: number | string } | undefined)?.id;
  const id = poi.id ?? metadataId;
  return id == null ? null : `poi:${poi.type}:${id}`;
};

const getDrawerEntityMetadataId = (
  metadata: MapDrawerEntityForPoi['metadata'],
): string | number | undefined => {
  if ('featureId' in metadata) return metadata.featureId;
  if ('uuid' in metadata) return metadata.uuid;
  return undefined;
};

const getDrawerEntityPoiKey = (
  entity: MapDrawerEntityForPoi,
): string | null => {
  const { type, id, metadata } = entity;
  // Feature ids are unique per layer only, so external features key by their source.
  const scope = 'sourceId' in metadata ? metadata.sourceId : type;
  const featureId = id ?? getDrawerEntityMetadataId(metadata);
  return featureId == null ? null : `drawerEntityPoi:${scope}:${featureId}`;
};

/**
 * Stable identity for an action, used to locate the previously active feature in a
 * newly resolved stack. Null when the action has no id and so cannot be matched.
 */
export const getOverlappingFeatureKey = (
  action: OverlappingFeatureAction,
): string | null => {
  switch (action.category) {
    case 'routerEntity': {
      // Ignore query params if any.
      const [pathname] = action.path.split('?');
      return `routerEntity:${action.entityType}:${pathname}`;
    }
    case 'poi':
      return getPoiKey(action.poi);
    case 'drawerEntityPoi':
      return getDrawerEntityPoiKey(action.entity);
    default:
      return null;
  }
};

/**
 * Decides the stack state for a newly resolved click: continue from the active feature
 * when it is still under the click (what the Next button does), otherwise start at the
 * top. The new stack always replaces the old one, so it only lists what was clicked.
 */
export const resolveOverlappingFeaturesUpdate = ({
  previous,
  nextFeatures,
  allowCycling,
}: {
  previous: OverlappingFeaturesState;
  nextFeatures: OverlappingFeatureAction[];
  allowCycling: boolean;
}): OverlappingFeaturesState => {
  const resetState: OverlappingFeaturesState = {
    features: nextFeatures,
    activeIndex: 0,
  };

  if (!allowCycling || nextFeatures.length < 2) return resetState;

  const activeAction = previous.features[previous.activeIndex];
  const activeKey = activeAction
    ? getOverlappingFeatureKey(activeAction)
    : null;
  if (activeKey == null) return resetState;

  const activeIndexInNextFeatures = nextFeatures.findIndex(
    (action) => getOverlappingFeatureKey(action) === activeKey,
  );
  if (activeIndexInNextFeatures === -1) return resetState;

  return {
    features: nextFeatures,
    activeIndex: getCyclicIndex({
      index: activeIndexInNextFeatures,
      length: nextFeatures.length,
      step: 1,
    }),
  };
};
